import { Injectable, signal, effect, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'bellaspeak-theme';
  
  // Signal-based state management
  isDarkMode = signal<boolean>(false);
  
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTheme();
      
      // React to theme changes
      effect(() => {
        this.applyTheme(this.isDarkMode());
      });
    }
  }
  
  private initializeTheme(): void {
    const savedTheme = localStorage.getItem(this.THEME_KEY);
    
    if (savedTheme === 'dark' || savedTheme === 'light') {
      this.isDarkMode.set(savedTheme === 'dark');
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.isDarkMode.set(prefersDark);
    }
    
    // Listen for system theme changes
    this.setupSystemThemeListener();
  }
  
  private applyTheme(isDark: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const theme = isDark ? 'dark' : 'light';
    
    // Apply data-theme attribute (for your CSS)
    document.documentElement.setAttribute('data-theme', theme);
    
    // Apply class (additional styling if needed)
    document.body.classList.toggle('dark-theme', isDark);
    document.body.classList.toggle('light-theme', !isDark);
    
    // Save to localStorage
    localStorage.setItem(this.THEME_KEY, theme);
    
    // console.log('Theme applied:', theme); // Debug log
  }
  
  toggleTheme(): void {
    const newTheme = !this.isDarkMode();
    this.isDarkMode.set(newTheme);
    // console.log('Theme toggled to:', newTheme ? 'dark' : 'light'); // Debug log
  }
  
  setTheme(isDark: boolean): void {
    this.isDarkMode.set(isDark);
  }
  
  private setupSystemThemeListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      // Only update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem(this.THEME_KEY);
      if (!savedTheme) {
        this.isDarkMode.set(e.matches);
      }
    });
  }
}
